import React, { useCallback, useEffect, useMemo } from "react";
import CanvasKanban from "../ProjectList/CanvasKanban";
import { ProjectPageHeader } from "../ProjectList/ProjectPageHeader";
import { useViewManagement, useFilterOperations } from "./hooks";
import { FilterKey } from "@/types/filterEnums";
import { ViewForm } from "./components/ViewForm";
import { FilterBar } from "./components/FilterBar";
import { EmptyState } from "./components/EmptyState";
import { Layers2 } from "lucide-react";
import type { Project, ProjectWithStatuses } from "@/utils/types";
import type { FilterState } from "../ProjectList/types";
import { SavedView } from "./hooks/useViews";
import { getDefaultFilters } from "@/utils/filterUrlUtils";

interface ViewsPanelProps {
  project?: Project;
  selectedViewId?: string;
  onViewSelect?: (viewId: string) => void;
}

const PanelLayout = React.memo<{
  children: React.ReactNode;
  className?: string;
}>(({ children, className = "" }) => (
  <div className={`flex-1 overflow-auto bg-background ${className}`}>
    {children}
  </div>
));
PanelLayout.displayName = "PanelLayout";

const KanbanSection = React.memo<{
  project: Project | null;
  filters: FilterState | undefined;
  viewType: "grid" | "list";
  clearFilters: () => void;
}>(({ project, filters, viewType, clearFilters }) => (
  <div className="min-h-[400px]">
    <CanvasKanban
      project={project as ProjectWithStatuses | null}
      onSelect={() => {}}
      filters={filters}
      viewType={viewType}
      clearFilters={clearFilters}
    />
  </div>
));
KanbanSection.displayName = "KanbanSection";

const EmptyStateComponent = React.memo<{
  onCreateView: () => void;
}>(({ onCreateView }) => (
  <EmptyState
    icon={Layers2}
    title="No views found"
    actionLabel="View"
    onAction={onCreateView}
  />
));
EmptyStateComponent.displayName = "EmptyStateComponent";

const ViewFormSection = React.memo<{
  viewName: string;
  viewType: "grid" | "list";
  currentProject: Project | null;
  filters: FilterState | undefined;
  onNameChange: (name: string) => void;
  onViewTypeChange: (type: "grid" | "list") => void;
  onSave: () => void;
  onUpdateFilters: (newFilters: Partial<FilterState>) => void;
  clearFilters: () => void;
}>(
  ({
    viewName,
    viewType,
    currentProject,
    filters,
    onNameChange,
    onViewTypeChange,
    onSave,
    onUpdateFilters,
    clearFilters,
  }) => (
    <div>
      <ViewForm
        viewName={viewName}
        onNameChange={onNameChange}
        onSave={onSave}
      />
      <ProjectPageHeader
        project={currentProject}
        setViewType={onViewTypeChange}
        viewType={viewType}
        showInviteButton={false}
        filters={filters || getDefaultFilters()}
        updateFilters={onUpdateFilters}
      />
      <KanbanSection
        project={currentProject}
        filters={filters}
        viewType={viewType}
        clearFilters={clearFilters}
      />
    </div>
  ),
);
ViewFormSection.displayName = "ViewFormSection";

const CurrentViewSection = React.memo<{
  currentProject: Project | null;
  viewType: "grid" | "list";
  hasActiveFilters: boolean;
  filters: FilterState | undefined;
  allViews: SavedView[];
  onViewTypeChange: (type: "grid" | "list") => void;
  onRemoveFilter: (filterType: FilterKey, value: string) => void;
  onRemoveEntireFilter: (filterType: FilterKey) => void;
  onUpdateFilter: (
    filterType: FilterKey,
    value: string | number,
    condition?: string,
  ) => void;
  onUpdateFilters: (newFilters: Partial<FilterState>) => void;
  clearFilters: () => void;
}>(
  ({
    currentProject,
    viewType,
    hasActiveFilters,
    filters,
    allViews,
    onViewTypeChange,
    onRemoveFilter,
    onRemoveEntireFilter,
    onUpdateFilter,
    onUpdateFilters,
    clearFilters,
  }) => (
    <div>
      <ProjectPageHeader
        project={currentProject}
        setViewType={onViewTypeChange}
        viewType={viewType}
        showInviteButton={false}
        filters={filters || getDefaultFilters()}
        updateFilters={onUpdateFilters}
      />
      {hasActiveFilters && filters && allViews.length > 0 && (
        <FilterBar
          filters={filters}
          currentProject={currentProject}
          onRemoveFilter={onRemoveFilter}
          onRemoveEntireFilter={onRemoveEntireFilter}
          onUpdateFilter={onUpdateFilter}
          updateFilters={onUpdateFilters}
        />
      )}
      <KanbanSection
        project={currentProject}
        filters={filters}
        viewType={viewType}
        clearFilters={clearFilters}
      />
    </div>
  ),
);
CurrentViewSection.displayName = "CurrentViewSection";

const ViewsPanel: React.FC<ViewsPanelProps> = React.memo(
  ({ project, selectedViewId, onViewSelect }) => {
    const {
      isCreatingView,
      viewName,
      viewType,
      currentView,
      allViews,
      hasActiveFilters,
      filters,
      currentProject,
      setViewName,
      setViewType,
      setIsCreatingView,
      handleSaveView,
      handleUpdateFilter,
      updateFilters,
    } = useViewManagement({ selectedViewId, onViewSelect, project });

    const {
      removeFilter,
      removeEntireFilter,
      resetFilters,
      updateConditionsForValues,
    } = useFilterOperations(currentProject?.id, filters);

    const clearFilters = resetFilters;

    useEffect(() => {
      if (
        allViews.length > 0 &&
        !selectedViewId &&
        !isCreatingView &&
        onViewSelect
      ) {
        onViewSelect(allViews[0].id);
      }
    }, [allViews, selectedViewId, isCreatingView, onViewSelect]);

    useEffect(() => {
      if (!filters || !currentProject?.id) return;

      if (filters.userIds)
        updateConditionsForValues(FilterKey.USER_IDS, filters.userIds);
      if (filters.labels)
        updateConditionsForValues(FilterKey.LABELS, filters.labels);
      if (filters.statuses)
        updateConditionsForValues(FilterKey.STATUSES, filters.statuses);
      if (filters.estimates)
        updateConditionsForValues(FilterKey.ESTIMATES, filters.estimates);
      if (filters.priorities)
        updateConditionsForValues(FilterKey.PRIORITIES, filters.priorities);
      if (filters.dueDates) {
        const dueDateValues = Object.entries(filters.dueDates)
          .filter(([_, value]) => value)
          .map(([key, _]) => key);
        updateConditionsForValues(FilterKey.DUE_DATES, dueDateValues);
      }
    }, [filters, currentProject?.id, updateConditionsForValues]);

    const handleCreateView = useCallback(() => {
      setIsCreatingView(true);
      clearFilters();
    }, [setIsCreatingView, clearFilters]);

    const shouldShowEmptyState = useMemo(
      () => allViews.length === 0 && !isCreatingView,
      [allViews.length, isCreatingView],
    );

    const shouldShowCreateForm = useMemo(
      () => isCreatingView,
      [isCreatingView],
    );

    const shouldShowCurrentView = useMemo(
      () => currentView && !isCreatingView,
      [currentView, isCreatingView],
    );

    if (shouldShowEmptyState) {
      return (
        <PanelLayout>
          <EmptyStateComponent onCreateView={handleCreateView} />
        </PanelLayout>
      );
    }

    if (shouldShowCreateForm) {
      return (
        <PanelLayout>
          <ViewFormSection
            viewName={viewName}
            viewType={viewType}
            currentProject={currentProject}
            filters={filters}
            onNameChange={setViewName}
            onViewTypeChange={setViewType}
            onSave={handleSaveView}
            onUpdateFilters={updateFilters}
            clearFilters={clearFilters}
          />
        </PanelLayout>
      );
    }

    if (shouldShowCurrentView) {
      return (
        <PanelLayout>
          <CurrentViewSection
            currentProject={currentProject}
            viewType={viewType}
            hasActiveFilters={hasActiveFilters}
            filters={filters}
            allViews={allViews}
            onViewTypeChange={setViewType}
            onRemoveFilter={removeFilter}
            onRemoveEntireFilter={removeEntireFilter}
            onUpdateFilter={handleUpdateFilter}
            onUpdateFilters={updateFilters}
            clearFilters={clearFilters}
          />
        </PanelLayout>
      );
    }

    return (
      <PanelLayout>
        <EmptyStateComponent onCreateView={handleCreateView} />
      </PanelLayout>
    );
  },
);

ViewsPanel.displayName = "ViewsPanel";

export default ViewsPanel;
